import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useTranslation } from 'react-i18next';

import axiosInstance from '../../axiosConfig';

const VerificationCodeField = forwardRef(function VerificationCodeField(
  {
    challengePath = '/Guest/TrackChallenge',
    value,
    onValueChange,
    onChallengeIdChange,
    disabled = false,
  },
  ref,
) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [challengeImage, setChallengeImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadChallenge = useCallback(async (resetValue = true) => {
    setLoading(true);
    setError('');

    try {
      const challenge = await axiosInstance.get(challengePath);
      setChallengeImage(challenge.imageDataUri || '');
      onChallengeIdChange?.(challenge.challengeId || '');

      if (resetValue) {
        onValueChange('');
      }
    } catch {
      setChallengeImage('');
      onChallengeIdChange?.('');
      setError(t('verificationCode_loadError'));
    } finally {
      setLoading(false);
    }
  }, [challengePath, onChallengeIdChange, onValueChange, t]);

  useImperativeHandle(ref, () => ({
    refresh: loadChallenge,
  }), [loadChallenge]);

  useEffect(() => {
    loadChallenge();
  }, [loadChallenge]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 3,
        borderColor: alpha(theme.palette.primary.main, 0.22),
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, #ffffff 100%)`,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { md: 220 } }}>
          <Box sx={{
            width: 46,
            height: 46,
            borderRadius: 2.5,
            background: alpha(theme.palette.primary.main, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ShieldOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Box>
            <Typography fontWeight={800} sx={{ fontSize: '0.95rem' }}>
              {t('verificationCode_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('verificationCode_hint')}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ flex: 1 }}>
          <Box sx={{
            minWidth: { xs: '100%', sm: 176 },
            height: 68,
            borderRadius: 2.5,
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.28)}`,
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {loading ? (
              <CircularProgress size={24} />
            ) : challengeImage ? (
              <Box component="img" src={challengeImage} alt={t('verificationCode_alt')} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('verificationCode_loadError')}
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            label={t('verificationCode_label')}
            placeholder={t('verificationCode_placeholder')}
            value={value}
            onChange={(event) => onValueChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
            size="small"
            required
            disabled={disabled}
            inputProps={{ maxLength: 4, inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />

          <Button
            type="button"
            variant="outlined"
            onClick={() => loadChallenge()}
            disabled={disabled || loading}
            startIcon={<AutorenewRoundedIcon />}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', minWidth: { sm: 128 } }}
          >
            {t('verificationCode_refresh')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Typography variant="body2" color="error.main" sx={{ mt: 1.5 }}>
          {error}
        </Typography>
      )}
    </Paper>
  );
});

export default VerificationCodeField;
